import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, MessageCircle, CheckCircle2, Quote, Sparkles } from "lucide-react";
import { COMPANY, HERO_IMAGE, SERVICES, STATS, TESTIMONIALS, ABOUT_IMAGE, TESTIMONIALS_BG } from "@/lib/data";
import ServiceCard from "@/components/ServiceCard";
import AnimatedCounter from "@/components/AnimatedCounter";
import SectionHeading from "@/components/SectionHeading";
import MeasurementDivider from "@/components/MeasurementDivider";

const headlineWordsTop = ["Präzise", "Gutachten."];
const headlineWordsBottom = ["Fundierte", "Entscheidungen."];

const TRUST = [
  "Schnelle Terminvergabe",
  "Unabhängig & neutral",
  "Persönliche Beratung",
  "WhatsApp verfügbar",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[100svh] flex items-center overflow-hidden bg-messinger-blue"
      >
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="KFZ-Inspektion in Werkstatt"
            className="w-full h-full object-cover animate-ken-burns"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-black/30" />
        </div>

        <div className="container-x relative pt-32 pb-24 lg:pt-40 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="h-px w-12 bg-messinger-orange" />
              <span className="text-[11px] tracking-[0.28em] uppercase font-semibold text-messinger-orange">
                KFZ-Sachverständiger · Castrop-Rauxel
              </span>
            </motion.div>

            <h1 className="font-heading font-semibold tracking-tight text-white leading-[1.02] text-5xl sm:text-6xl lg:text-[88px] text-balance">
              <span className="block">
                {headlineWordsTop.map((w, i) => (
                  <motion.span
                    key={w}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 + i * 0.1 }}
                    className="inline-block mr-3"
                  >
                    {w}
                  </motion.span>
                ))}
              </span>
              <span className="block text-messinger-orange">
                {headlineWordsBottom.map((w, i) => (
                  <motion.span
                    key={w}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 + i * 0.1 }}
                    className="inline-block mr-3"
                  >
                    {w}
                  </motion.span>
                ))}
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-8 max-w-xl text-lg text-white/80 leading-relaxed"
            >
              Unfallgutachten, Fahrzeugbewertungen und Oldtimer-Expertise —
              schnell, unabhängig, persönlich.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.85 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/kontakt"
                data-testid="hero-cta-termin"
                className="cta-orange group inline-flex items-center justify-center gap-2 px-8 py-4 bg-messinger-orange text-white font-semibold tracking-tight rounded-full text-sm transition-colors"
              >
                Termin anfragen
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/leistungen"
                data-testid="hero-cta-leistungen"
                className="cta-ghost group inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white hover:text-messinger-blue font-semibold tracking-tight rounded-full text-sm transition-colors"
              >
                Leistungen entdecken
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.05 }}
              className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 max-w-2xl"
            >
              {TRUST.map((t) => (
                <div key={t} className="flex items-center gap-2 text-white/80 text-xs sm:text-sm">
                  <CheckCircle2 size={14} className="text-messinger-orange flex-shrink-0" />
                  <span>{t}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Floating speedometer-card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="hidden lg:block lg:col-span-4 relative"
          >
            <div className="relative bg-white/[0.04] backdrop-blur-md border border-white/15 rounded-3xl p-6">
              <div className="flex items-center justify-between mb-6">
                <span className="text-[10px] tracking-[0.28em] uppercase text-messinger-orange font-semibold">
                  Profil
                </span>
                <Sparkles size={14} className="text-messinger-orange" />
              </div>
              <div className="space-y-5">
                <div>
                  <div className="text-3xl font-heading font-semibold text-white">
                    Mike Messinger
                  </div>
                  <div className="text-sm text-white/60 mt-1">
                    KFZ-Sachverständiger & KFZ-Meister
                  </div>
                </div>
                <div className="h-px w-full bg-white/10" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-white/50 uppercase tracking-[0.18em]">
                      Büro
                    </div>
                    <div className="text-sm text-white mt-1">
                      {COMPANY.address.street}
                      <br />
                      {COMPANY.address.zip} {COMPANY.address.city}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white/50 uppercase tracking-[0.18em]">
                      Reaktion
                    </div>
                    <div className="text-sm text-white mt-1">
                      Innerhalb 24 Std.
                    </div>
                  </div>
                </div>
                <div className="h-px w-full bg-white/10" />
                <a
                  href={COMPANY.phoneHref}
                  className="flex items-center justify-between text-sm text-white hover:text-messinger-orange transition-colors group"
                >
                  <span>Direkt anrufen</span>
                  <Phone size={16} className="text-messinger-orange transition-transform group-hover:translate-x-1" />
                </a>
                <a
                  href={COMPANY.whatsappHref}
                  className="flex items-center justify-between text-sm text-white hover:text-messinger-orange transition-colors group"
                >
                  <span>Per WhatsApp</span>
                  <MessageCircle size={16} className="text-messinger-orange transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-6 inset-x-0 container-x flex justify-between items-center text-white/40 text-[10px] tracking-[0.28em] uppercase">
          <span>{`Standort · ${COMPANY.address.city}`}</span>
          <span>Scroll ↓</span>
        </div>
      </section>

      {/* STATS */}
      <section data-testid="stats-section" className="relative bg-messinger-bg py-20 lg:py-24 border-b border-white/5">
        <div className="container-x relative">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
            {STATS.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative pl-6"
                data-testid={`stat-${i}`}
              >
                <div className="absolute left-0 top-2 bottom-2 w-px bg-messinger-orange" />
                <div className="font-heading text-4xl lg:text-5xl font-bold text-white tracking-tight">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="mt-2 text-sm text-white/55 tracking-tight">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="relative bg-messinger-surface py-24 lg:py-36 overflow-hidden">
        <div className="ambient-glow ambient-glow-orange w-[500px] h-[500px] -top-32 -left-32" />
        <div className="ambient-glow ambient-glow-blue w-[600px] h-[600px] bottom-0 -right-40 opacity-30" />
        <div className="container-x relative">
          <SectionHeading
            eyebrow="Leistungen"
            title="Sechs Kompetenzen."
            titleAccent="Ein Ansprechpartner."
            intro="Vom Unfallgutachten bis zur Oldtimerbewertung — jede Leistung führe ich persönlich durch. Direkter Kontakt, keine Subunternehmer."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {SERVICES.map((s, i) => (
              <ServiceCard key={s.slug} service={s} index={i} />
            ))}
          </div>
          <div className="mt-12 flex">
            <Link
              to="/leistungen"
              data-testid="services-view-all"
              className="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.14em] uppercase text-white hover:text-messinger-orange transition-colors"
            >
              Alle Leistungen ansehen
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <MeasurementDivider label="Stimmen aus dem Einzugsgebiet" />

      {/* TESTIMONIALS */}
      <section
        data-testid="testimonials-section"
        className="relative py-24 lg:py-32 bg-messinger-bg overflow-hidden"
      >
        <div className="absolute inset-0 opacity-[0.08]">
          <img
            src={TESTIMONIALS_BG}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-messinger-bg via-messinger-bg/70 to-messinger-bg" />
        <div className="container-x relative">
          <SectionHeading
            dark
            eyebrow="Kundenstimmen"
            title="Warum Kunden auf uns"
            titleAccent="setzen."
            intro="Drei kurze Einblicke aus der täglichen Arbeit — repräsentativ für die Art, wie wir mit jedem Anliegen umgehen."
          />
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="relative bg-white/[0.04] border border-white/10 rounded-3xl p-8 backdrop-blur-sm hover:border-white/20 transition-colors"
                data-testid={`testimonial-${i}`}
              >
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.2 + i * 0.15 }}
                >
                  <Quote size={32} className="text-messinger-orange mb-6" />
                </motion.div>
                <p className="text-white/85 leading-relaxed text-[15px] mb-8">
                  {t.text}
                </p>
                <div className="pt-6 border-t border-white/10">
                  <div className="text-white font-semibold text-sm">{t.name}</div>
                  <div className="text-white/50 text-xs tracking-[0.18em] uppercase mt-1">
                    {t.location}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section data-testid="about-teaser" className="bg-messinger-surface py-24 lg:py-36 relative overflow-hidden">
        <div className="ambient-glow ambient-glow-orange w-[500px] h-[500px] top-1/2 -translate-y-1/2 -right-32 opacity-40" />
        <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16 items-center relative">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-l-2 border-t-2 border-messinger-orange" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r-2 border-b-2 border-messinger-orange" />
              <div className="relative overflow-hidden bg-messinger-blue aspect-[4/5]">
                <img src={ABOUT_IMAGE} alt="Mike Messinger" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-black/40 to-transparent" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-messinger-orange" />
              <span className="label-eyebrow">Über Mike Messinger</span>
            </div>
            <h2 className="font-heading font-semibold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-balance">
              Einer für alles —{" "}
              <span className="text-messinger-orange">persönlich, direkt, kompetent.</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed text-lg">
              KFZ-Meister, Sachverständiger und Einzelunternehmer. Bei mir
              sprechen Sie immer direkt mit der Person, die Ihr Gutachten
              erstellt — kein Callcenter, keine Subunternehmer, kein doppelter
              Boden. Das schafft Vertrauen, spart Zeit und liefert
              nachvollziehbare Ergebnisse.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Über 20 Jahre Werkstatt- und Sachverständigen-Erfahrung",
                "Persönliche Begutachtung — keine Subunternehmer",
                "Beweissichere Dokumentation für Versicherung und Gericht",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3 text-sm text-white/80">
                  <CheckCircle2 size={18} className="text-messinger-orange mt-0.5 flex-shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/ueber-uns"
              data-testid="about-teaser-link"
              className="mt-10 inline-flex items-center gap-2 text-sm font-semibold tracking-[0.14em] uppercase text-white hover:text-messinger-orange transition-colors"
            >
              Mehr über mich
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section data-testid="cta-banner" className="relative py-24 lg:py-32 bg-black overflow-hidden border-y border-white/5">
        <div className="ambient-glow ambient-glow-orange w-[600px] h-[600px] -top-40 -right-40" />
        <div className="container-x relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-8 bg-messinger-orange" />
                <span className="label-eyebrow">Kontakt</span>
              </div>
              <h2 className="font-heading font-semibold text-white text-4xl lg:text-5xl leading-[1.05] text-balance">
                Jetzt unverbindlich{" "}
                <span className="text-messinger-orange">anfragen.</span>
              </h2>
              <p className="mt-5 text-white/70 max-w-xl text-lg">
                Schreiben Sie mir per Formular oder WhatsApp — innerhalb von
                24 Stunden erhalten Sie eine persönliche Rückmeldung.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link
                to="/kontakt"
                data-testid="cta-banner-form"
                className="cta-orange group inline-flex items-center justify-between px-6 py-4 bg-messinger-orange text-white font-semibold rounded-full text-sm transition-colors"
              >
                <span>Zum Kontaktformular</span>
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={COMPANY.whatsappHref}
                data-testid="cta-banner-whatsapp"
                className="group inline-flex items-center justify-between px-6 py-4 border border-white/30 text-white hover:border-messinger-orange hover:text-messinger-orange font-semibold rounded-full text-sm transition-colors"
              >
                <span>WhatsApp schreiben</span>
                <MessageCircle size={18} className="transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href={COMPANY.phoneHref}
                data-testid="cta-banner-call"
                className="group inline-flex items-center justify-between px-6 py-4 border border-white/30 text-white hover:border-messinger-orange hover:text-messinger-orange font-semibold rounded-full text-sm transition-colors"
              >
                <span>Direkt anrufen</span>
                <Phone size={18} className="transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
